import { inject } from 'vue'

/** What a host provides as `onFundraisingError` to receive failures from these islands. */
export type FundraisingErrorReporter = (error: unknown, context: string) => void

export const FUNDRAISING_ERROR_KEY = 'onFundraisingError'

/** An identifier-ish token — `SESSION_EXPIRED`, `bank.declined`. Free text is not: a vendor string
 *  like "Account 8000-1234567 is not permitted" would sail through a length cap with the account
 *  still in it, so anything that isn't a bare token is reduced to its length. */
const SAFE_TOKEN = /^[A-Za-z_][\w.-]{0,39}$/

/**
 * Describes an unknown value without carrying it. Vendor payloads reach reporting from callbacks
 * typed `unknown`, and the shapes that matter are exactly the ones `Object.keys` is useless on:
 * an Error enumerates to nothing, a string enumerates to character indices.
 */
export function describeUnknown(value: unknown): string {
  if (value instanceof Error) {
    return SAFE_TOKEN.test(value.message)
      ? `${value.name}: ${value.message}`
      : `${value.name}(len=${value.message.length})`
  }
  if (typeof value === 'string') {
    return SAFE_TOKEN.test(value) ? value : `string(len=${value.length})`
  }
  if (typeof value !== 'object' || value === null) return String(typeof value)
  const keys = Object.keys(value)
  if (keys.length) return `keys=${keys.join(',')}`
  // Class instances and DOM events enumerate to nothing; their constructor is the only signal left.
  const name = (value as { constructor?: { name?: string } }).constructor?.name
  return name && name !== 'Object' ? name : 'empty'
}

/**
 * Anything that isn't already an Error becomes one, describing its shape rather than carrying it.
 * Rollbar clones a non-Error argument wholesale into an occurrence's custom data (`createItem` in
 * rollbar's utility.js), so a raw payload passed here would be reported in full — the donor's bank
 * account with it. Normalising at the boundary means no call site can make that mistake, and it
 * also gets Rollbar a stack, which it only extracts from real Errors.
 */
function toReportable(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(`non-Error thrown (${describeUnknown(error)})`)
}

/** The floor a host cannot fall below: what the components did before this channel existed. */
function logToConsole(error: unknown, context: string): void {
  console.error(`[fundraising] ${context}`, error)
}

/**
 * Fire-and-forget reporting to the host, which decides what an error and a context mean — its own
 * `flagError` / `$rollbar` / `window.Rollbar`. The package itself stays framework-agnostic.
 *
 * Not the components' `error` emits: those drive the message the donor reads, and this has to work
 * from components several levels below a flow, where an emit would need re-raising at every step.
 * Injected for the same reason `t` is: one wiring point per host.
 *
 * An unwired host falls back to the console rather than to nothing. Swallowing is not the same as
 * silent: without that floor, a host whose reporter is missing or broken is indistinguishable from
 * one with nothing to report — which is the inference failure this channel exists to remove.
 */
export function useFundraisingError(): FundraisingErrorReporter {
  const report = inject<FundraisingErrorReporter>(FUNDRAISING_ERROR_KEY, logToConsole)
  return (error, context) => {
    const reportable = toReportable(error)
    try {
      report(reportable, context)
    } catch (reporterFailure) {
      // A broken reporter must not take down the flow it observes — but it must not vanish either,
      // or a host with faulty wiring looks exactly like a host with nothing to report.
      logToConsole(reportable, context)
      console.error('[fundraising] error reporter threw', reporterFailure)
    }
  }
}
